import {
  AUTOMATIC_REPLY_NOTICE,
  INTERNAL_NOTE_MARK,
  SENDER_AUTHENTICATION_NOTICES,
  rejectedAttachmentsNotice,
  TICKET_CHANNEL_LABELS,
  TICKET_INBOUND_MESSAGE_KIND_LABELS,
  TICKET_RECIPIENT_SOURCE_LABELS,
  TICKET_STATE_LABELS,
  UNRECOGNIZED_SENDER_NOTICE,
  operatorRoleLabel,
  threadEntryAnchor,
  type TicketInboundEntry,
  type TicketMessageAuthor,
  type TicketNoteEntry,
  type TicketReplyEntry,
  type TicketReportEntry,
} from '@easyhelp/shared';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { formatDateTime, personName } from '../../components/ticket';
import { DeliveryChip } from './DeliveryChip';
import { MESSAGE_BUBBLES } from './message-bubbles';
import { MessageAttachmentList } from './MessageAttachmentList';
import { QuotedText } from './QuotedText';
import { THREAD_ENTRY_ATTRIBUTE, THREAD_TARGET_SX } from './thread-anchor';
import type { TicketThreadMessage } from './thread-groups';
import { ThreadText } from './ThreadText';
import { ViaMcpMark } from './ViaMcp';

/**
 * Resa dei messaggi del thread come conversazione (#121, decisione del product owner del
 * 16/10/2026, linguaggio visivo di `ThreadView` e `MessageBubble` della vetrina doctor-inbox, AUDIT-001
 * § Thread e composer): chi ha segnalato a sinistra su `grey.100`, gli operatori a destra su
 * `primary.lighter`, raggio 8 e al più l'80% del pannello; sotto `sm` le bolle occupano tutta la
 * larghezza e si distinguono per colore e chip di ruolo. Le note interne non sono bolle: righe a
 * tutta larghezza nello stile di `DiarioNoteListItem`. Gli eventi di sistema stanno in
 * `ThreadEvent.tsx` e il loro raggruppamento in `TicketThread.tsx` (#120).
 */

/** Chip di ruolo di chi ha segnalato, accanto al nome (#121). */
const REPORTER_ROLE_LABEL = 'Segnalante';

/** Etichetta della nota interna nel thread (#121), con la marcatura «Non visibile all'utente». */
const INTERNAL_NOTE_LABEL = 'Nota interna';

/**
 * Le proprietà che ogni voce del thread deve avere (#70): l'`id` dell'ancora del link delle
 * notifiche, il focus solo da script, l'attributo delle voci e lo stile dell'evidenza.
 */
function threadEntryProps(entry: Pick<TicketThreadMessage, 'kind' | 'id'>) {
  return {
    component: 'li',
    id: threadEntryAnchor(entry),
    // Focalizzabile solo da script: aprendo il link di una notifica il focus arriva qui (#70).
    tabIndex: -1,
    [THREAD_ENTRY_ATTRIBUTE]: '',
  } as const;
}

/**
 * Chip di ruolo su fondo bianco, così si legge su entrambe le bolle e sulla nota: «Segnalante» in
 * grigio, il ruolo dell'operatore nel colore primario. Con l'allineamento non è l'unico segnale.
 */
function RoleChip({ label, operator = false }: { label: string; operator?: boolean }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={operator ? 'primary' : 'default'}
      label={label}
      sx={{ bgcolor: 'common.white' }}
    />
  );
}

/** Il ruolo e il livello di un operatore, nel chip accanto al nome: «Analista · 1° livello». */
function OperatorRoleChip({ author }: { author: TicketMessageAuthor }) {
  return <RoleChip operator label={operatorRoleLabel(author)} />;
}

/** Nome dell'autore: un'email lunga senza spazi va a capo invece di uscire dalla voce (#119). */
function AuthorName({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" component="span" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  );
}

/** Momento e canale sotto il nome: «02/10/2026, 11:00 · Replica via email». */
function MetaLine({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" component="p" color="text.secondary" sx={{ m: 0, overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  );
}

function MessageBubble({
  entry,
  variant,
  author,
  chips,
  meta,
  children,
}: {
  /** Il messaggio: ne dà l'`id` DOM, l'ancora del link delle notifiche (`m-<id>`, #70). */
  entry: Pick<TicketThreadMessage, 'kind' | 'id'>;
  variant: keyof typeof MESSAGE_BUBBLES;
  author: string;
  /** Chip di ruolo ed etichette accanto al nome (mittente non riconosciuto, esito della consegna). */
  chips: ReactNode;
  meta: string;
  children?: ReactNode;
}) {
  const bubble = MESSAGE_BUBBLES[variant];
  return (
    <Box
      {...threadEntryProps(entry)}
      data-variant={variant}
      sx={{
        listStyle: 'none',
        // Da `sm` in su da un lato e al più l'80%; sotto, a tutta larghezza (si allunga nella lista):
        // restano il colore e il chip di ruolo.
        alignSelf: { sm: bubble.side },
        maxWidth: { sm: '80%' },
        minWidth: 0,
        px: 4,
        py: 3,
        bgcolor: bubble.bgcolor,
        color: 'text.primary',
        borderRadius: 2,
        ...THREAD_TARGET_SX,
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
            <AuthorName>{author}</AuthorName>
            {chips}
          </Stack>
          <MetaLine>{meta}</MetaLine>
        </Stack>
        {children}
      </Stack>
    </Box>
  );
}

function ReportMessage({ entry }: { entry: TicketReportEntry }) {
  return (
    <MessageBubble
      entry={entry}
      variant="utente"
      author={entry.author}
      chips={<RoleChip label={REPORTER_ROLE_LABEL} />}
      meta={`${formatDateTime(entry.at)} · Segnalazione ${TICKET_CHANNEL_LABELS[entry.channel].toLowerCase()}`}
    >
      <ThreadText text={entry.text} />
    </MessageBubble>
  );
}

/**
 * Messaggio dell'utente arrivato per email (#42): replica di chi ha segnalato o email collegata
 * dalla posta non associata. Testo semplice ripulito dalla citazione (mai HTML); dichiara il
 * mittente non riconosciuto, gli allegati rifiutati, il testo troncato e l'arrivo su un ticket
 * terminale. Un mittente non riconosciuto non è «Segnalante»: al posto del chip di ruolo ha quello
 * di avviso.
 */
function InboundMessage({ entry }: { entry: TicketInboundEntry }) {
  const unrecognized = entry.kind === 'utente_non_riconosciuto';
  const authentication = entry.senderAuthentication === 'superata' ? null : entry.senderAuthentication;
  const notices = [
    // Il motivo della «non riconosciuta»: autenticazione fallita oppure mittente diverso.
    authentication ? SENDER_AUTHENTICATION_NOTICES[authentication] : null,
    unrecognized && authentication !== 'fallita' ? UNRECOGNIZED_SENDER_NOTICE : null,
    entry.automatic ? AUTOMATIC_REPLY_NOTICE : null,
    entry.arrivedOnTerminalState
      ? `Arrivata su un ticket «${TICKET_STATE_LABELS[entry.arrivedOnTerminalState]}»: annotata senza effetti sullo stato, nessun nuovo ticket.`
      : null,
    entry.rejectedAttachments > 0 ? rejectedAttachmentsNotice(entry.rejectedAttachments) : null,
    entry.truncated ? 'Testo troppo lungo: mostrato solo l’inizio.' : null,
  ].filter((notice): notice is string => notice !== null);
  const channel = entry.origin === 'collegata' ? 'Email collegata dalla posta non associata' : 'Replica via email';
  return (
    <MessageBubble
      entry={entry}
      variant="utente"
      author={entry.sender}
      chips={
        <>
          {unrecognized ? (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={TICKET_INBOUND_MESSAGE_KIND_LABELS[entry.kind]}
              // `warning.main` su bianco è sotto 4.5:1: il testo prende `warning.dark`.
              sx={{ bgcolor: 'common.white', color: 'warning.dark' }}
            />
          ) : (
            <RoleChip label={REPORTER_ROLE_LABEL} />
          )}
          {entry.automatic && <RoleChip label="Risposta automatica" />}
        </>
      }
      meta={`${formatDateTime(entry.at)} · ${channel}`}
    >
      {entry.text ? (
        <ThreadText text={entry.text} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nessun testo oltre la citazione dell’email precedente.
        </Typography>
      )}
      {notices.map((notice) => (
        <Typography key={notice} variant="caption" color="text.secondary" role="note">
          {notice}
        </Typography>
      ))}
      {entry.quotedText && <QuotedText text={entry.quotedText} />}
    </MessageBubble>
  );
}

function ReplyMessage({ entry, code }: { entry: TicketReplyEntry; code: string }) {
  return (
    <MessageBubble
      entry={entry}
      variant="supporto"
      author={personName(entry.author)}
      chips={
        <>
          <OperatorRoleChip author={entry.author} />
          <DeliveryChip delivery={entry.delivery} />
        </>
      }
      meta={`${formatDateTime(entry.at)} · Risposta via email a ${entry.recipient} (${TICKET_RECIPIENT_SOURCE_LABELS[entry.recipientSource]})`}
    >
      <ThreadText text={entry.text} />
      <MessageAttachmentList code={code} attachments={entry.attachments} />
    </MessageBubble>
  );
}

/**
 * Nota interna nello stile di `DiarioNoteListItem` (EasyCare): non una bolla ma una riga a tutta
 * larghezza fra due fili, con l'etichetta col lucchetto e la marcatura «Non visibile all'utente»,
 * l'autore col suo ruolo e il momento a destra.
 */
function NoteMessage({ entry, code }: { entry: TicketNoteEntry; code: string }) {
  return (
    <Box
      {...threadEntryProps(entry)}
      data-variant="nota"
      sx={{
        listStyle: 'none',
        px: 2,
        py: 4,
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'grey.600',
        ...THREAD_TARGET_SX,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Chip size="small" icon={<LockOutlinedIcon />} label={INTERNAL_NOTE_LABEL} />
          <Chip size="small" variant="outlined" label={INTERNAL_NOTE_MARK} />
          <AuthorName>{personName(entry.author)}</AuthorName>
          {entry.viaMcp && <ViaMcpMark />}
          <OperatorRoleChip author={entry.author} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(entry.at)}
          </Typography>
        </Stack>
        <ThreadText text={entry.text} />
        <MessageAttachmentList code={code} attachments={entry.attachments} />
      </Stack>
    </Box>
  );
}

/**
 * Un messaggio del thread come `li` con l'ancora del link delle notifiche (#70). Risposte e note
 * mostrano i loro allegati (#43), scaricabili dal ticket `code`; i messaggi dell'utente non ne hanno.
 */
export function ThreadMessage({ entry, code }: { entry: TicketThreadMessage; code: string }) {
  switch (entry.kind) {
    case 'segnalazione':
      return <ReportMessage entry={entry} />;
    case 'risposta':
      return <ReplyMessage entry={entry} code={code} />;
    case 'nota':
      return <NoteMessage entry={entry} code={code} />;
    case 'utente':
    case 'utente_non_riconosciuto':
      return <InboundMessage entry={entry} />;
  }
}
